import { getLabelsFromZipCityEstateId } from '@portals/sip-client-data/src/general/ApiClientMapping/ApiClientMapping';
import { EstateSearchProps } from '@portals/sip-client-data/src/general/ApiClientTypes';
import { ConfigProvider } from '@portals/sip-client-data/src/general/Config';
import { getFixedT } from 'i18next';
import { ParsedUrlQuery } from 'querystring';
import { useCallback, useEffect, useState } from 'react';

import { Storage } from '../../utils';

export type UseSearchIsSaved = {
  saveButtonLabelFormatter: (EstateSearchProps) => string;
  toggleSearchIsSaved: (EstateSearchProps) => void;
  loadSavedSearches: () => void;
  savedSearches: Array<{ id: number; params: ParsedUrlQuery }>;
};

const t = getFixedT(null, 'core-immobilien');

export const STORAGE_KEY_SEARCH_AGENTS = 'search-agents';
const STORAGE_KEY_SEARCH_AGENTS_SIP = `${STORAGE_KEY_SEARCH_AGENTS}_sip`;
const STORAGE_KEY_SEARCH_AGENTS_IMMOBILIEN = `${STORAGE_KEY_SEARCH_AGENTS}_immobilien`;

export const getSearchAgentsStorageKey = (): string => {
  // TODO: REMOVE AFTER IMMOBILIEN API INTEGRATION: switch
  const useImmobilienApi = ConfigProvider.getConfig().get('USE_IMMOBILIEN_API') === 'true';
  return useImmobilienApi ? STORAGE_KEY_SEARCH_AGENTS_IMMOBILIEN : STORAGE_KEY_SEARCH_AGENTS_SIP;
};

export const useSearchIsSaved = (): UseSearchIsSaved => {
  const [savedSearches, setSavedSearches] = useState<Array<{ searchParams: ParsedUrlQuery }>>([]);

  const loadSavedSearches = () => {
    const saved = Storage.get(getSearchAgentsStorageKey()) || [];
    setSavedSearches(saved);
  };

  useEffect(() => {
    loadSavedSearches();
  }, []);

  const searchExists = useCallback(
    (searchParams: EstateSearchProps): boolean => {
      return savedSearches.some((search) => deepEqual(search.searchParams, searchParams));
    },
    [savedSearches]
  );

  const toggleSearchIsSaved = async (searchParams: EstateSearchProps) => {
    const convertedParams = convertSearchParamsForApiRequest(searchParams);
    let updatedSearches;

    if (searchExists(convertedParams)) {
      updatedSearches = savedSearches.filter((search) => !deepEqual(search.searchParams, convertedParams));
    } else {
      updatedSearches = [...savedSearches, { searchParams: convertedParams }];
    }

    setSavedSearches(updatedSearches);
    Storage.set(getSearchAgentsStorageKey(), updatedSearches);
  };

  const saveButtonLabelFormatter = useCallback(
    (searchParams: EstateSearchProps) => {
      const convertedParams = convertSearchParamsForApiRequest(searchParams);

      return searchExists(convertedParams) ? t('savedSearch') : t('saveSearch');
    },
    [searchExists]
  );

  // TODO: Hier wird als Rückgabe noch für savedSearches: Array<{ id: number; params: ParsedUrlQuery }> erwartet
  const convertSavedSearchesForReturn = (savedSearches: Array<{ searchParams: ParsedUrlQuery }>) => {
    return savedSearches.map((search, index) => ({
      id: index,
      params: search.searchParams,
    }));
  };

  return {
    saveButtonLabelFormatter,
    toggleSearchIsSaved,
    loadSavedSearches,
    savedSearches: convertSavedSearchesForReturn(savedSearches),
  };
};

function convertSearchParamsForApiRequest(searchParams: EstateSearchProps): ParsedUrlQuery {
  if (searchParams.zipCityEstateId) {
    searchParams.zipCityEstateId = getLabelsFromZipCityEstateId(searchParams.zipCityEstateId);
  }
  return convertToNumbers(removeSortBy(searchParams));
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const removeSortBy = ({ sortBy, ...rest }: EstateSearchProps) => rest;

const deepEqual = (obj1: EstateSearchProps, obj2: EstateSearchProps): boolean => {
  if (!obj1 || !obj2) {
    return false;
  }

  return JSON.stringify(removeSortBy(obj1)) === JSON.stringify(removeSortBy(obj2));
};

const convertToNumbers = (searchParams) => {
  const convertedParams = { ...searchParams };
  const numberFields = getNumberFieldsOfEstateSearchProps();

  numberFields.forEach((field) => {
    const value = convertedParams[field];

    if (convertedParams[field] && typeof convertedParams[field] === 'string') {
      const numValue = Number(value);
      if (isNaN(numValue)) {
        delete convertedParams[field];
      } else {
        convertedParams[field] = numValue;
      }
    }
  });

  return convertedParams;
};

const getNumberFieldsOfEstateSearchProps = (): (keyof EstateSearchProps)[] => {
  return [
    'perimeter',
    'limit',
    'page',
    'estateTypeId',
    'minPrice',
    'maxPrice',
    'minRent',
    'maxRent',
    'minMarketValue',
    'maxMarketValue',
    'minLease',
    'maxLease',
    'minLivingSpace',
    'maxLivingSpace',
    'minPropertySize',
    'maxPropertySize',
    'minTotalSpace',
    'maxTotalSpace',
    'minNumberRooms',
    'maxNumberRooms',
  ];
};
